import React, { useEffect, useRef, useState } from "react";

export default function NudgePanel({ show, onClose, employeeData }) {
  const [nudgeMessage, setNudgeMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sendStatus, setSendStatus] = useState(null); // "success" | "error"
  const [retryAfterSec, setRetryAfterSec] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (show && employeeData) {
      setNudgeMessage("");
      setError("");
      setSendStatus(null);
      setRetryAfterSec(null);
      generateNudge();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, employeeData]);

  useEffect(() => {
    if (!retryAfterSec || retryAfterSec <= 0) return;
    const t = setInterval(() => {
      setRetryAfterSec((s) => (typeof s === "number" ? Math.max(0, s - 1) : s));
    }, 1000);
    return () => clearInterval(t);
  }, [retryAfterSec]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [nudgeMessage]);

  const generateNudge = async () => {
    if (!employeeData) return;
    try {
      setLoading(true);
      setError("");
      setRetryAfterSec(null);

      const res = await fetch("/api/nudge/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(employeeData),
      });

      const json = await res.json();

      if (!res.ok) {
        const detail = json?.detail;
        if (res.status === 429) {
          const msg =
            (typeof detail === "object" && detail?.message) ||
            (typeof detail === "string" && detail) ||
            "AI quota exceeded. Please try again shortly.";
          const ra =
            typeof detail === "object" && typeof detail?.retry_after_seconds === "number"
              ? detail.retry_after_seconds
              : null;
          if (ra) setRetryAfterSec(ra);
          throw new Error(msg);
        }
        throw new Error(
          (typeof detail === "string" && detail) ||
            (typeof detail === "object" && detail?.message) ||
            "Failed to generate nudge"
        );
      }

      if (json.status === "success") {
        setNudgeMessage(json.message);
      } else {
        throw new Error("Unexpected response from server");
      }
    } catch (err) {
      setError(err?.message || "Something went wrong while generating");
    } finally {
      setLoading(false);
    }
  };

  const sendNudge = async () => {
    if (!nudgeMessage.trim()) return;
    try {
      setSending(true);
      setSendStatus(null);

      const res = await fetch("/api/nudge/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          employee_empcode: employeeData?.empCode,
          message: nudgeMessage,
        }),
      });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.detail || "Failed to send nudge");
      }
      setSendStatus("success");
    } catch (err) {
      setSendStatus("error");
      setError(err?.message || "Something went wrong while sending");
    } finally {
      setSending(false);
    }
  };

  if (!show) return null;

  const initials = employeeData?.name
    ? employeeData.name.trim().split(/\s+/).map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  const metrics = employeeData?.performanceMetrics;

  return (
    <>
      <div style={s.backdrop} onClick={onClose} />
      <div style={s.panel}>
        <div style={s.header}>
          <div>
            <div style={s.headerTitle}>Send Nudge</div>
            <div style={s.headerSub}>via WhatsApp</div>
          </div>
          <button style={s.closeBtn} onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        {employeeData && (
          <div style={s.empCard}>
            <div style={s.avatar}>{initials}</div>
            <div style={{ flex: 1 }}>
              <div style={s.empName}>{employeeData.name}</div>
              <div style={s.empMeta}>
                {employeeData.empCode} · {employeeData.hq}
              </div>
            </div>

            {metrics && (
              <div style={s.statsPill}>
                <span style={s.statItem}>
                  <span style={s.statLabel}>Sales</span>
                  <span style={getAchvColor(metrics.salesAch?.percentage)}>{metrics.salesAch?.percentage ?? "—"}%</span>
                </span>
                <span style={s.divider} />
                <span style={s.statItem}>
                  <span style={s.statLabel}>Cov</span>
                  <span style={getAchvColor(metrics.coverage?.percentage)}>{metrics.coverage?.percentage ?? "—"}%</span>
                </span>
              </div>
            )}
          </div>
        )}

        <div style={s.section}>
          <div style={s.sectionHeader}>
            <span style={s.sectionTitle}>Message Preview</span>
            {!loading && nudgeMessage && (
              <button style={s.regenBtn} onClick={generateNudge} title="Regenerate">
                ↺ Regenerate
              </button>
            )}
          </div>

          {loading ? (
            <div style={s.loadingBox}>
              <div style={s.spinner} />
              <span style={{ color: "#888", fontSize: "13px" }}>Generating message…</span>
            </div>
          ) : error && !nudgeMessage ? (
            <div style={s.errorBox}>
              <span>⚠ {error}</span>
              {typeof retryAfterSec === "number" && retryAfterSec > 0 ? (
                <button style={{ ...s.retryBtn, opacity: 0.65, cursor: "not-allowed" }} disabled>
                  Retry in {retryAfterSec}s
                </button>
              ) : (
                <button style={s.retryBtn} onClick={generateNudge}>
                  Retry
                </button>
              )}
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              style={s.textarea}
              value={nudgeMessage}
              onChange={(e) => {
                setNudgeMessage(e.target.value);
                setSendStatus(null);
              }}
              placeholder="Message will appear here…"
              disabled={loading}
            />
          )}
        </div>

        {sendStatus === "success" && <div style={s.successBanner}>✅ Nudge sent successfully via WhatsApp!</div>}
        {sendStatus === "error" && <div style={s.errorBanner}>❌ {error || "Failed to send. Please try again."}</div>}

        <div style={s.actions}>
          <button
            style={{
              ...s.whatsappBtn,
              opacity: sending || loading || !nudgeMessage.trim() ? 0.6 : 1,
              cursor: sending || loading || !nudgeMessage.trim() ? "not-allowed" : "pointer",
            }}
            onClick={sendNudge}
            disabled={sending || loading || !nudgeMessage.trim()}
          >
            {sending ? (
              <span>Sending…</span>
            ) : (
              <>
                <WhatsAppIcon />
                <span>Send via WhatsApp</span>
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

function WhatsAppIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.849L.057 23.57a.75.75 0 00.912.912l5.723-1.471A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.891 0-3.667-.5-5.203-1.376l-.374-.213-3.397.873.89-3.395-.226-.381A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
    </svg>
  );
}

function getAchvColor(pct) {
  const n = parseFloat(pct);
  const color = n >= 95 ? "#22c55e" : n >= 80 ? "#f59e0b" : "#ef4444";
  return { color, fontWeight: 700, fontSize: "13px" };
}

const s = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    zIndex: 999,
  },
  panel: {
    position: "fixed",
    top: 0,
    right: 0,
    width: "400px",
    height: "100%",
    background: "#fff",
    boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    animation: "aiSalesDiagnosisSlideIn 0.22s ease",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "20px 20px 16px",
    borderBottom: "1px solid #f0f0f0",
    background: "#fafafa",
  },
  headerTitle: { fontWeight: 700, fontSize: "16px", color: "#111" },
  headerSub: { fontSize: "12px", color: "#999", marginTop: "2px" },
  closeBtn: {
    border: "none",
    background: "transparent",
    fontSize: "18px",
    cursor: "pointer",
    color: "#aaa",
    padding: "2px 6px",
    lineHeight: 1,
  },
  empCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px 20px",
    borderBottom: "1px solid #f0f0f0",
  },
  avatar: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "15px",
    flexShrink: 0,
  },
  empName: { fontWeight: 600, fontSize: "14px", color: "#111" },
  empMeta: { fontSize: "12px", color: "#888", marginTop: "2px" },
  statsPill: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#f5f5f5",
    borderRadius: "20px",
    padding: "6px 12px",
    fontSize: "12px",
  },
  statItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: "1px" },
  statLabel: { fontSize: "10px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px" },
  divider: { width: "1px", height: "20px", background: "#ddd" },
  section: { flex: 1, padding: "16px 20px" },
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" },
  sectionTitle: { fontWeight: 600, fontSize: "13px", color: "#444" },
  regenBtn: {
    border: "1px solid #e0e0e0",
    background: "transparent",
    color: "#666",
    fontSize: "11px",
    cursor: "pointer",
    padding: "3px 10px",
    borderRadius: "20px",
  },
  loadingBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "32px",
    background: "#fafafa",
    borderRadius: "8px",
  },
  spinner: {
    width: "24px",
    height: "24px",
    border: "3px solid #f0f0f0",
    borderTop: "3px solid #667eea",
    borderRadius: "50%",
    animation: "aiSalesDiagnosisSpin 0.7s linear infinite",
  },
  errorBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px",
    padding: "14px",
    background: "#fff5f5",
    borderRadius: "8px",
    color: "#c0392b",
    fontSize: "13px",
    border: "1px solid #fdd",
  },
  retryBtn: {
    border: "1px solid #c0392b",
    background: "transparent",
    color: "#c0392b",
    fontSize: "12px",
    cursor: "pointer",
    padding: "4px 12px",
    borderRadius: "4px",
  },
  textarea: {
    width: "100%",
    minHeight: "130px",
    padding: "12px",
    fontSize: "13px",
    lineHeight: "1.6",
    border: "1px solid #e8e8e8",
    borderRadius: "8px",
    resize: "vertical",
    outline: "none",
    fontFamily: "inherit",
    color: "#222",
    background: "#fdfdfd",
    boxSizing: "border-box",
  },
  successBanner: {
    margin: "0 20px 12px",
    padding: "10px 14px",
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: "8px",
    color: "#166534",
    fontSize: "13px",
  },
  errorBanner: {
    margin: "0 20px 12px",
    padding: "10px 14px",
    background: "#fff5f5",
    border: "1px solid #fdd",
    borderRadius: "8px",
    color: "#c0392b",
    fontSize: "13px",
  },
  actions: {
    padding: "16px 20px 24px",
    borderTop: "1px solid #f0f0f0",
    background: "#fafafa",
  },
  whatsappBtn: {
    width: "100%",
    padding: "13px",
    background: "#25d366",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
};

if (typeof document !== "undefined" && !document.getElementById("ai-sales-diagnosis-nudge-keyframes")) {
  const style = document.createElement("style");
  style.id = "ai-sales-diagnosis-nudge-keyframes";
  style.textContent = `
    @keyframes aiSalesDiagnosisSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
    @keyframes aiSalesDiagnosisSpin { to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(style);
}

